import React, {useRef} from 'react';
import cx from 'classnames';
import NextLink from 'next/link';

import {Button, Text} from '#component/base';
import RectangleSkeleton from '#component/base/skeletons/RectangleSkeleton';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {SubscriptionStatusEnum} from '#util/subscriptions';

import {ReactivateSubscriptionButton} from './ReactivateSubscriptionButton';
import {SkipNextOrderModal} from './SkipNextOrderModal/SkipNextOrderModal';
import {useCTACustomerState} from './useCTACustomerState';

import * as styles from './CTASubscription.css';
import {
  btnIndividualTester,
  btnIndividualTesterBottomText,
  btnWrapper,
  ctaBottomContentBox,
  ctaBottomContentBoxText,
  ctaContentBox,
  ctaHeadingContentBox,
  ctaNonMemNonSubContentBox,
  ctaNonMemNonSubContentBoxMobile,
  ctaNonMemNonSubContentBoxTextBottom,
  ctaSingleSubscriptionWrapper,
  ctaSubscriptionWrapper,
  mainCtaSubscriptionTag,
  nonMemNonSubHeaderContainer,
  nonMemNonSubHeadingContainer,
  nonMemNonSubHeadingContainerMobile,
  nonMemNonSubSubheaderContainer,
  secondaryCtaSubscription,
  secondaryCtaSubscriptionInner,
  secondaryCtaSubscriptionInnerContent,
  secondaryCtaSubscriptionInnerContentBtn,
  secondaryCtaSubscriptionInnerContentCaption,
} from './CTASubscription.css';

const CTASubscription = () => {
  const divRef = useRef<HTMLDivElement>(null);
  const {
    heading,
    subHeading,
    subHeadingMobile,
    status,
    subscription,
    loading,
    bgMain,
    bgTablet,
    bgMobile,
    bgUpSell,
    textColor,
    textColorSubscriptionStatus,
    textSinceColor,
    headingSecond,
    subheadingSecond,
    textColorSecond,
    textColorSubHeadingSecond,
    secondBtnText,
    secondBtnLink,
    textSince,
    canceledOrExpired,
    secondBtnVariant = 'primary',
    upcomingOrder,
    productVariants,
    skipBtnVariant = 'secondary',
    bgImage,
    bgImageTablet,
    bgImageMobile,
    align,
  } = useCTACustomerState();

  const {isMobile, width} = useViewportSize();

  const {track} = useAnalytics();

  const handleTrackClick = (actionName: string) => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName,
    });
  };

  {
    /* Logic to verify if Non-Member and Non-Subscriber, or CANCELED Member and/or Subscriber.
  If not, it falls into the second condition, which can be either ACTIVE or PAUSED Member and/or Subscriber. */
  }
  const displayTextBottom = !subscription && width < 970 && !isMobile;
  const contentBoxClassName =
    !subscription || (canceledOrExpired && status !== 'ACTIVE')
      ? isMobile
        ? displayTextBottom
          ? ctaNonMemNonSubContentBoxTextBottom
          : ctaNonMemNonSubContentBoxMobile
        : displayTextBottom
        ? ctaNonMemNonSubContentBoxTextBottom
        : ctaNonMemNonSubContentBox
      : ctaContentBox;

  const getBackgroundImageUrl = () => {
    if (isMobile) {
      return bgImageMobile;
    } else {
      return bgImage;
    }
  };

  let backgroundImageStyle = {
    backgroundImage: `url('${getBackgroundImageUrl()}')`,
    backgroundPosition: isMobile ? 'right top' : 'right',
    backgroundSize: 'cover',
  };

  if (!subscription && !isMobile && width < 970) {
    backgroundImageStyle = {
      backgroundImage: `url('${bgImageTablet}')`,
      backgroundPosition: 'right top',
      backgroundSize: '100%',
    };
  }

  return !loading ? (
    <div
      className={
        headingSecond && secondBtnLink ? ctaSubscriptionWrapper : ctaSingleSubscriptionWrapper
      }>
      {/* Account Status Card */}
      <div
        ref={divRef}
        className={cx(
          styles.mainCtaSubscriptionMember,
          displayTextBottom && bgTablet ? bgTablet : isMobile && bgMobile ? bgMobile : bgMain,
        )}
        style={{...backgroundImageStyle}}>
        <div className={contentBoxClassName}>
          {/* Status Flag */}
          {status ? (
            <Text
              variant="button"
              color={textColorSubscriptionStatus}
              className={cx(
                mainCtaSubscriptionTag,
                status === SubscriptionStatusEnum.ACTIVE && sprinkles({bg: 'solidGreen'}),
                status === SubscriptionStatusEnum.PAUSED && sprinkles({bg: 'dustyGray'}),
                (!status || SubscriptionStatusEnum.CANCELLED) && sprinkles({bg: 'pink'}),
              )}>
              {status}
            </Text>
          ) : null}

          <div className={ctaHeadingContentBox}>
            {/* Handling for individual testers who do not have a membership or supplement subscription */}
            {!subscription ? (
              /* Header and Content for Non-Member and Non-Subscriber or CANCELED Member and/or Subscriber */
              <div
                className={
                  isMobile || displayTextBottom
                    ? nonMemNonSubHeadingContainerMobile
                    : nonMemNonSubHeadingContainer
                }>
                {/* Header */}
                <div className={nonMemNonSubHeaderContainer}>
                  <Text
                    as={isMobile || displayTextBottom ? undefined : 'h2'}
                    style={{
                      fontSize: isMobile || displayTextBottom ? '32px' : undefined,
                      color: textColor,
                      lineHeight: isMobile || displayTextBottom ? '1.2' : undefined,
                      fontWeight: isMobile || displayTextBottom ? 80 : undefined,
                    }}
                    variant={isMobile || displayTextBottom ? undefined : 'heading2'}>
                    {heading}
                  </Text>
                </div>
                {/* Subheader / Card Content */}
                <div className={nonMemNonSubSubheaderContainer}>
                  <Text
                    as={isMobile || displayTextBottom ? undefined : 'p'}
                    style={
                      isMobile || displayTextBottom
                        ? {fontSize: '14px', color: textColor}
                        : undefined
                    }
                    variant={isMobile || displayTextBottom ? undefined : 'body'}
                    color={textColor}>
                    {isMobile && subHeadingMobile ? subHeadingMobile : subHeading?.[0]}
                  </Text>
                </div>
              </div>
            ) : (
              /* Header and Content for ACTIVE, PAUSED, or CANCELED Member and/or Subscriber */
              <div>
                {/* Header */}
                <Text as="h2" variant="heading2" color={textColor}>
                  {heading}
                </Text>
                {/* Subheader / Card Content */}
                {subHeading.map((name, index) => (
                  <Text as="h3" variant="heading3" color={textColor} key={index}>
                    {name}
                  </Text>
                ))}
              </div>
            )}
          </div>
          {/* Copy Above Button */}
          <div className={ctaBottomContentBox}>
            {subscription && (
              <Text
                as="p"
                variant="body"
                color={textSinceColor}
                className={ctaBottomContentBoxText}>
                {textSince}
              </Text>
            )}

            {/* Buttons */}
            <div className={btnWrapper}>
              {/* Button logic for Non-Member and Non-Subscriber or ACTIVE/CANCELED Member and/or Subscriber */}
              {(status === SubscriptionStatusEnum.ACTIVE || !subscription || canceledOrExpired) && (
                <>
                  {/* Skip Button only for ACTIVE Member or ACTIVE Subscriber with upcoming order */}
                  {upcomingOrder &&
                    subscription &&
                    productVariants &&
                    subscription.status === SubscriptionStatusEnum.ACTIVE && (
                      <SkipNextOrderModal
                        productVariants={productVariants}
                        skipBtnVariant={skipBtnVariant}
                      />
                    )}
                  {/* Button for Non-Member and Non-Subscriber or CANCELED Member and/or Subscriber */}
                  {(status !== SubscriptionStatusEnum.ACTIVE ||
                    !subscription ||
                    canceledOrExpired) &&
                  headingSecond ? (
                    <div>
                      <NextLink href="/profile?tab=manage" passHref>
                        <Button
                          as="a"
                          variant="primary"
                          icon="ArrowRight"
                          onClick={() => handleTrackClick('Click Select a Plan')}>
                          Select a Plan
                        </Button>
                      </NextLink>
                    </div>
                  ) : !headingSecond && !secondBtnLink ? (
                    /* Button for Individual Tester */
                    <NextLink href="/shop/collections" passHref>
                      <Button
                        as="a"
                        variant="primary"
                        icon="ArrowRight"
                        className={
                          displayTextBottom ? btnIndividualTesterBottomText : btnIndividualTester
                        }>
                        Shop Now
                      </Button>
                    </NextLink>
                  ) : (
                    /* Button for ACTIVE Member or Subscriber */
                    <NextLink href="/profile?tab=manage" passHref>
                      <Button
                        as="a"
                        variant="primary"
                        icon="ArrowRight"
                        onClick={() => handleTrackClick('Click Manage My Plan')}>
                        Manage My Plan
                      </Button>
                    </NextLink>
                  )}
                </>
              )}
              {/* Buttons for PAUSED Member or Subscriber */}
              {status === SubscriptionStatusEnum.PAUSED && (
                <>
                  <ReactivateSubscriptionButton textColor={textColor} />
                  <NextLink href="/profile?tab=manage" passHref>
                    <Button as="a" variant="secondary" color={textColor}>
                      Manage My Plan
                    </Button>
                  </NextLink>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {headingSecond && secondBtnLink ? (
        <div className={cx(secondaryCtaSubscription, bgUpSell)}>
          <div
            className={cx(secondaryCtaSubscriptionInner, align === 'bottom' && styles.alignBottom)}>
            <div className={secondaryCtaSubscriptionInnerContent}>
              <Text as="h3" variant="heading3" color={textColorSecond}>
                {headingSecond}
              </Text>
              <Text
                as="p"
                variant="button"
                color={textColorSubHeadingSecond}
                className={secondaryCtaSubscriptionInnerContentCaption}>
                {subheadingSecond}
              </Text>
            </div>
            <NextLink href={secondBtnLink} passHref>
              <Button
                as="a"
                variant={secondBtnVariant}
                className={secondaryCtaSubscriptionInnerContentBtn({secondBtnVariant})}>
                {secondBtnText}
              </Button>
            </NextLink>
          </div>
        </div>
      ) : null}
    </div>
  ) : (
    <div className={sprinkles({flex: 'flex', marginTop: 40, width: 'full', dir: ['col', 'row']})}>
      <RectangleSkeleton height={500} width={750} />
      <RectangleSkeleton height={500} width={450} />
    </div>
  );
};

export default CTASubscription;
